import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type {
  GetDashboardNewLeadsQuery,
  GetDashboardNewLeadsQueryVariables,
} from '@graphql/__generated__/graphql';
import { SegmentedToggleButtonGroup } from '@modules/_shared/components/SegmentedToggleButtonGroup';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { subscribeFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { DashboardLeadsCardItem } from '@modules/dashboard/dashboard-lead/DashboardLeadsCardItem';
import { GET_DASHBOARD_NEW_LEADS } from '@modules/dashboard/dashboardQueries';
import { useDashboardList } from '@modules/dashboard/hooks/useDashboardList';
import { DashboardCard } from '@modules/dashboard/shared/DashboardCard';
import { useLocale } from '@shared/hooks/locale';

const VIEW_ALL_LEADS_LINK = `/leads?where=${JSON.stringify({
  completed: { _eq: true },
  lead_agents: {
    lead_stage: {
      status: {
        _in: ['active'],
      },
    },
  },
})}`;

export const DashboardLeadsCard = () => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [activeTab, setActiveTab] = useState(0);

  const isTeamLeader = me?.teams?.some(t => t.is_leader) ?? false;

  const threeMonthsAgo = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  }, []);

  const teamIds = useMemo(() => {
    if (!isTeamLeader || !me?.teams) {
      return [];
    }
    return me.teams.filter(t => t.is_leader).map(t => t.team_id);
  }, [me?.teams, isTeamLeader]);

  const hasTeamLeadsQuery = isTeamLeader && teamIds.length > 0;

  const { myLeadsQueryVariables, teamLeadsQueryVariables } = useMemo(() => {
    const baseConditions = [
      { created_at: { _gte: threeMonthsAgo } },
      { claimed_at: { _is_null: true } },
      { passed_at: { _is_null: true } },
    ];

    const myLeadsWhere = {
      _and: [{ user_id: { _eq: me?.id } }, ...baseConditions],
    };

    const teamLeadsWhere = {
      _and: [
        { user_id: { _neq: me?.id } },
        { user: { teams_users: { team_id: { _in: teamIds } } } },
        ...baseConditions,
      ],
    };

    return {
      myLeadsQueryVariables: {
        where: { lead_agents: myLeadsWhere },
        leadAgentsWhere: myLeadsWhere,
      },
      teamLeadsQueryVariables: {
        where: { lead_agents: teamLeadsWhere },
        leadAgentsWhere: teamLeadsWhere,
      },
    };
  }, [me?.id, teamIds, threeMonthsAgo]);

  const myLeadsQuery = useDashboardList<
    GetDashboardNewLeadsQuery,
    GetDashboardNewLeadsQueryVariables,
    GetDashboardNewLeadsQuery['leads'][number]
  >({
    query: GET_DASHBOARD_NEW_LEADS,
    variables: myLeadsQueryVariables,
    dataKey: 'leads',
    countKey: 'leads_aggregate',
    fetchPolicy: 'network-only',
    skip: !me?.id,
  });

  const teamLeadsQuery = useDashboardList<
    GetDashboardNewLeadsQuery,
    GetDashboardNewLeadsQueryVariables,
    GetDashboardNewLeadsQuery['leads'][number]
  >({
    query: GET_DASHBOARD_NEW_LEADS,
    variables: teamLeadsQueryVariables,
    dataKey: 'leads',
    countKey: 'leads_aggregate',
    fetchPolicy: 'network-only',
    skip: !hasTeamLeadsQuery,
  });

  const refetchMyLeads = myLeadsQuery.refetch;
  const refetchTeamLeads = teamLeadsQuery.refetch;

  useEffect(() => {
    const unsubscribe = subscribeFeedRefresh(() => {
      if (me?.id) {
        refetchMyLeads();
      }
      if (hasTeamLeadsQuery) {
        refetchTeamLeads();
      }
    });

    return unsubscribe;
  }, [me?.id, hasTeamLeadsQuery, refetchMyLeads, refetchTeamLeads]);

  const showToggle =
    isTeamLeader && (teamLeadsQuery.loading || teamLeadsQuery.totalCount > 0);
  const effectiveTab = showToggle ? activeTab : 0;

  const prevShowToggle = useRef(showToggle);
  useEffect(() => {
    if (prevShowToggle.current && !showToggle) {
      setActiveTab(0);
    }
    prevShowToggle.current = showToggle;
  }, [showToggle]);

  const activeQuery = effectiveTab === 1 ? teamLeadsQuery : myLeadsQuery;

  const handleRemove = useCallback(() => {
    activeQuery.refetch();
  }, [activeQuery]);

  const toggle = showToggle ? (
    <SegmentedToggleButtonGroup
      value={activeTab}
      onChange={setActiveTab}
      options={[
        { value: 0, label: `${t('Mine')} (${myLeadsQuery.totalCount})` },
        { value: 1, label: `${t('Team')} (${teamLeadsQuery.totalCount})` },
      ]}
      sx={{
        ml: 1,
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.75rem',
          py: 0.25,
          px: 1,
          lineHeight: 1.5,
          borderColor: 'grey.400',
          color: 'text.primary',
          bgcolor: 'grey.100',
          '&:hover': {
            bgcolor: 'grey.200',
          },
          '&.Mui-selected': {
            bgcolor: 'primary.main',
            color: 'common.white',
            borderColor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          },
        },
      }}
    />
  ) : null;

  return (
    <DashboardCard
      title={t('New seller leads')}
      titleAdornment={toggle}
      actionLabel={t('View all leads')}
      actionLink={VIEW_ALL_LEADS_LINK}
      queryResult={activeQuery}
      renderItem={(lead, key) => (
        <DashboardLeadsCardItem
          key={key}
          lead={lead}
          showAssignedAgents={effectiveTab === 1}
          onRemove={handleRemove}
        />
      )}
      emptyStateMessage={
        effectiveTab === 1
          ? t('Your team does not have any new leads to claim.')
          : t('You do not have any new leads to claim.')
      }
      emptyStateVariant="success"
    />
  );
};
